/**
 * Icon tooltip behaviour, shared so only one tooltip is open at a time.
 * - Desktop (pointer devices): open on hover.
 * - Mobile (no hover): toggle on tap, close on tap elsewhere.
 *
 * The consuming element must use `@click.stop` on its wrapper so an open tap
 * does not immediately bubble to the document-level close handler.
 */
const activeId = ref<number | null>(null)
let counter = 0
let listenerAttached = false

export function useIconTooltip() {
  const id = ++counter
  const isOpen = computed(() => activeId.value === id)
  const canHover = ref(true)

  function open() {
    activeId.value = id
  }
  function close() {
    if (activeId.value === id) activeId.value = null
  }
  function toggle() {
    activeId.value = isOpen.value ? null : id
  }

  function onPointerEnter() {
    if (canHover.value) open()
  }
  function onPointerLeave() {
    if (canHover.value) close()
  }
  function onClick() {
    if (!canHover.value) toggle()
  }

  onMounted(() => {
    canHover.value = window.matchMedia('(hover: hover)').matches
    if (!listenerAttached) {
      document.addEventListener('click', () => {
        activeId.value = null
      })
      listenerAttached = true
    }
  })

  onBeforeUnmount(close)

  return { isOpen, canHover, onPointerEnter, onPointerLeave, onClick }
}
